import { gql, useQuery } from '@apollo/client';
import {
  Button,
  Code,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React from 'react';

import { Form as FormWrapper } from 'src/components/common/form/Form';
import { Loading } from 'src/components/common/loading/Loading';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import {
  PQListRepublishModalQuery,
  PQListRepublishModalQueryVariables,
} from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { PersistedQueryListPublishInfo } from '../../persistedQueryListConfigWizard/persistedQueryListPublishModal/PersistedQueryListPublishInfo';

interface Props {
  isOpen: boolean;
  graphId: string;
  listId: string;
  onClose: () => void;
  __test?: {
    loading?: boolean;
    error?: boolean;
  };
}

const pqListRepublishModalQuery = gql<
  PQListRepublishModalQuery,
  PQListRepublishModalQueryVariables
>`
  query PQListRepublishModalQuery($graphId: ID!, $listId: ID!) {
    graph(id: $graphId) {
      id
      persistedQueryList(id: $listId) {
        id
        name
        description
        linkedVariants {
          id
          name
        }
      }
    }
  }
`;

export const PersistedQueryListRepublishModal = ({
  isOpen,
  graphId,
  listId,
  onClose,
  __test,
}: Props) => {
  const {
    data: pqListData,
    loading: pqListLoading,
    error: pqListError,
  } = useQuery(pqListRepublishModalQuery, {
    variables: { graphId, listId },
  });

  const linkedVariants = pqListData?.graph?.persistedQueryList?.linkedVariants;
  const singleLinkedVariant =
    linkedVariants &&
    isMinLengthArray(0, linkedVariants) &&
    linkedVariants.length === 1
      ? linkedVariants[0]
      : undefined;

  return (
    <Modal isOpen={isOpen} size="xl" onClose={onClose}>
      <ModalOverlay />
      <FormWrapper onSubmit={onClose} noValidate>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Publish Operations</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <p>
              Your persisted query list has been created with the following ID:
              <Code canCopy>{listId ?? '<unknown list>'}</Code>
            </p>
            {__test?.loading || pqListLoading ? (
              <Loading />
            ) : __test?.error || pqListError ? (
              <ErrorMessage />
            ) : (
              <section>
                <PersistedQueryListPublishInfo
                  graphId={graphId}
                  listId={listId}
                  linkedVariantId={singleLinkedVariant?.id}
                />
              </section>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" type="submit">
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </FormWrapper>
    </Modal>
  );
};
